<template>
    <div class="main-bg-color">
        <div id="header"
            class="flex justify-center items-center px-12 mx-auto w-[50%] md:w-['30vw'] lg:w-[29%] xl:w-[20%] h-40 md:h-32 lg:h-28 bg-white rounded-xl">
            <img :src="advertiser.logoUrl" :alt="advertiser.name" class="h-24 w-auto object-contain" />
        </div>

        <div class="p-4">
            <div
                class="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 px-1 xl:px-20 2xl:px-40 gap-4">
                <div class="flex items-center gap-4">
                    <span class="font-semibold text-lg">Markets:</span>
                    <div class="flex gap-2">
                        <div v-for="market in uniqueMarkets" :key="market" v-tooltip.top="getCountryName(market)"
                            class="flex items-center">
                            <span :class="marketClass(market)"></span>
                        </div>
                    </div>
                </div>

                <div class="flex items-center gap-2">
                    <span class="font-semibold">Vertical:</span>
                    <SelectButton v-model="selectedVertical" :options="availableVerticals" optionLabel="name"
                        optionValue="value" class="vertical-select" v-tooltip.top="getVerticalTooltip" />
                </div>
            </div>

            <DataTable :value="transformedMarketDeals" v-model:expandedRows="expandedRows" dataKey="marketName"
                :scrollHeight="'58vh'" class="px-1 xl:px-20 2xl:px-40">
                <Column expander :headerStyle="headerStyles">

                </Column>
                <!-- Market Column -->
                <Column field="marketName" header="Market" :headerStyle="headerStyles">
                    <template #body="{ data }">
                        <div v-tooltip.left="getCountryName(data.marketName)">
                            <span :class="marketClass(data.marketName)" class="ml-2"></span>
                            {{ getCountryName(data.marketName) }}
                        </div>
                    </template>
                </Column>

                <!-- Nested Deals DataTable -->
                <template #expansion="slotProps">
                    <DataTable :value="slotProps.data.deals"
                        v-model:expandedRows="expandedDealsPerMarket[slotProps.data.marketName]" dataKey="dealId"
                        class="deals-datatable">
                        <Column expander style="width: 5rem" :headerStyle="nestedHeaderStyles"></Column>
                        <Column field="dealType" header="Type" style="width: 15%" :headerStyle="nestedHeaderStyles">
                            <template #body="{ data }">
                                {{ data.dealType.toUpperCase() }}
                            </template>
                        </Column>
                        <Column field="dealDescription" header="Deal" style="width: 45%"
                            :headerStyle="nestedHeaderStyles">
                            <template #body="{ data }">
                                <!-- {{ data.isPublic ? 'Up to' : '' }} -->
                                {{ getFriendlyDealMessage(data.customerDeal) }}
                            </template>
                        </Column>
                        <Column style="width: 40%; text-align: left;" :headerStyle="nestedHeaderStyles">
                            <template #body="{ data }">
                                <Button icon="pi pi-search" :label="`Request for ${itemTypeLabelPlural}`"
                                    class="p-button-outlined p-button-secondary p-button-sm"
                                    @click="expandDeal(data, slotProps.data.marketName)"
                                    :badge="getEligibleSitesCount(data, slotProps.data.marketName)"
                                    badgeClass="p-badge-info" />
                            </template>
                        </Column>

                        <!-- Add nested expansion for eligible sites -->
                        <template #expansion="dealSlotProps">
                            <div class="p-4">
                                <DataTable :value="[
                                    ...getEligibleSitesForDeal(dealSlotProps.data, slotProps.data.marketName),
                                    ...((!store.getters.isSeo) ? [newSiteRows[dealSlotProps.data.dealId] || defaultNewSiteRow] : [])
                                ]" :scrollHeight="'40vh'" scrollable class="shadow-sm sites-datatable">
                                    <Column field="name" header="Site Name">
                                        <template #body="{ data }">
                                            <div v-if="data.isNewSite && !data.isEditing">
                                                <Button icon="pi pi-plus" label="Add new toplist" severity="secondary"
                                                    outlined
                                                    @click="startNewSite(slotProps.data.marketName, dealSlotProps.data.dealType, dealSlotProps.data.dealId)"
                                                    class="p-button-sm p-button-outlined" />
                                            </div>
                                            <div v-else-if="data.isNewSite && data.isEditing">
                                                <InputText v-model="data.name" placeholder="Enter site name"
                                                    class="p-inputtext-sm" />
                                            </div>
                                            <template v-else>
                                                {{ data.name }}
                                            </template>
                                        </template>
                                    </Column>
                                    <Column field="url" header="URL"></Column>
                                    <Column field="affiliateLink" header="Affiliate Link"></Column>
                                    <Column field="linkStatus" header="Link status">
                                        <template #body="slotProps">
                                            <Tag v-if="slotProps.data.existingLink" :value="slotProps.data.linkStatus"
                                                :severity="Utils.getLinkStatusSeverity(slotProps.data.linkStatus)" />
                                        </template>
                                    </Column>
                                    <Column header="Action">
                                        <template #body="{ data }">
                                            <div class="flex gap-2">
                                                <Button
                                                    v-if="!data.existingLink && !data.isNewSite || (data.isNewSite && data.isEditing)"
                                                    icon="pi pi-link" severity="info" class="p-button-sm"
                                                    @click="data.isEditing ? handleLinkSite(data, dealSlotProps.data, slotProps.data.marketName) : linkSite(data, dealSlotProps.data, slotProps.data.marketName)"
                                                    label="Get affiliate link" :loading="loadingSites[data.id]"
                                                    :disabled="loadingSites[data.id]" />
                                                <Button v-if="data.isEditing" icon="pi pi-times" severity="danger"
                                                    class="p-button-sm"
                                                    @click="cancelNewSite(dealSlotProps.data.dealId)" label="Cancel" />
                                            </div>
                                        </template>
                                    </Column>
                                </DataTable>
                            </div>
                        </template>
                    </DataTable>
                </template>
            </DataTable>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { marketClass } from '@/utils/markets';
import { capitalizeFirstLetter } from '@/utils/string';
import { useToast } from "primevue/usetoast";
import { getFriendlyDealMessage } from '@/utils/deals';
import { headerStyles, nestedHeaderStyles } from '@/global/headerStyles';
import { getCountryName } from '@/utils/countries';
import SitesService from '@/services/SitesService';
import Utils from '@/utils';
import AdvertiserService from '@/services/AdvertiserService';
import { useStore } from 'vuex';

const store = useStore();
const toast = useToast();
const expandedRows = ref({});
const sites = ref([]);
const expandedDealsPerMarket = ref({});
const selectedVertical = ref(null);
const loadingSites = ref({});
const itemTypeLabelPlural = computed(() => store.getters.isSeo ? 'Sites' : 'Toplists');
const props = defineProps({
    advertiser: {
        type: Object,
        required: true
    },
    deal: {
        type: Object,
        required: false,
        default: null
    }
});

const newSiteRows = ref({});

const startNewSite = (country, siteType, dealId) => {
    //somehow get the sitename from an api call. or from the existing sites
    newSiteRows.value[dealId] = {
        isNewSite: true,
        isEditing: true,
        name: `New Toplist - ${country}`,
        existingLink: false,
        type: siteType,
        country: country
    };
};

const handleLinkSite = async (site, deal, marketName) => {
    loadingSites.value[site.id] = true;
    console.log(site, deal, marketName);
    try {
        const response = await AdvertiserService.createToplistAndRequestAffiliateLink(
            props.advertiser.id,
            marketName,
            deal.voonixAdvertiserId,
            deal.dealId,
            deal.isPublic,
            deal.version,
            site.name,
            site.type
        );

        //if the response is 200, then we proceed with the below code
        if (response.status === 200) {
            const newSite = {
                country: response.data.site.country,
                type: response.data.site.type,
                createdAt: response.data.site.createdAt,
                id: response.data.site.id,
                name: response.data.site.name,
                url: response.data.site.url,
                advertisers: [
                    {
                        advertiserId: props.advertiser.id,
                        market: marketName,
                        status: response.data.advertiser.status,
                        deal: response.data.advertiser.deal,
                        dealId: response.data.advertiser.dealId,
                        version: response.data.advertiser.version,
                        voonixAdvertiserId: response.data.advertiser.voonixAdvertiserId,
                        affiliateLink: response.data.advertiser.affiliateLink,
                        createdAt: response.data.advertiser.createdAt,
                        siteId: response.data.advertiser.siteId
                    }
                ]
            };
            sites.value.push(newSite);
            newSiteRows.value[deal.dealId] = { ...defaultNewSiteRow };
        }
        else if (response.status === 201) {
            //here we expect to have a message in the response body.
            //populate the toast with the message
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: response.data.message,
                life: 3000
            });
        }
    } catch (ex) {
        if (ex.response?.status === 409) {
            toast.add({
                severity: 'error',
                summary: 'Duplicate Name',
                detail: 'Toplist name already exists, please choose a different name',
                life: 3000
            });
        } else {
            toast.add({
                severity: 'error',
                summary: 'Please try again later',
                detail: 'Could not create or request affiliate link for your toplist at this time',
                life: 3000
            });
        }
        console.error('Error creating or requesting affiliate link:', ex);
    } finally {
        loadingSites.value[site.id] = false;
    }
};

async function linkSite(site, deal, marketName) {
    // Set loading state for this specific site
    loadingSites.value[site.id] = true;
    try {
        const response = await AdvertiserService.requestAffiliateLink(
            store.getters.userId,
            props.advertiser.id,
            site.id,
            marketName,
            deal.voonixAdvertiserId,
            deal.dealId,
            deal.isPublic,
            deal.version
        );

        if (response.status === 200) {
            const siteIndex = sites.value.findIndex(s => s.id === site.id);
            console.log('siteIndex', siteIndex);
            if (siteIndex !== -1) {
                const updatedSite = {
                    ...sites.value[siteIndex],
                    advertisers: [
                        ...sites.value[siteIndex].advertisers,
                        {
                            advertiserId: props.advertiser.id,
                            market: marketName,
                            status: response.data.status,
                            deal: response.data.deal,
                            dealId: deal.dealId
                        }
                    ]
                };
                sites.value[siteIndex] = updatedSite;
            }
        }
        else if (response.status === 201) {
            //here we expect to have a message in the response body.
            //populate the toast with the message
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: response.data.message,
                life: 3000
            });
        }
        
    } catch (error) {
        console.error('Error requesting affiliate link:', error);
    } finally {
        // Clear loading state for this site
        loadingSites.value[site.id] = false;
    }
}

// Transform market deals for the table
const transformedMarketDeals = computed(() => {
    if (!props.advertiser.marketDeals) return [];

    return props.advertiser.marketDeals
        .map(market => ({
            marketName: market.marketName,
            // Filter deals by selected vertical
            deals: market.deals
                .filter(deal => deal.vertical === selectedVertical.value)
                .map(deal => ({
                    ...deal,
                    dealDescription: getFriendlyDealMessage(deal.customerDeal)
                }))
        }))
        // Remove markets that have no deals after filtering
        .filter(market => market.deals.length > 0);
});

async function fetchSites() {
    try {
        await SitesService.getUserApprovedSitesForAdvertisers().then((response) => {
            sites.value = response.data;
        });
    } catch (err) {
        console.error(err);
    } finally {
    }
}


// Add new method to get eligible sites
function getEligibleSitesForDeal(deal, marketName) {
    const eligibleSites = sites.value
        .filter(site => site.country === marketName && site.type === deal.dealType)
        .map(site => {
            // Check if site is already linked to this advertiser with this deal
            const existingLink = site.advertisers.find(advertiser =>
                advertiser.advertiserId === props.advertiser.id &&
                advertiser.dealId === deal.dealId &&
                advertiser.market === marketName &&
                advertiser.deal.commissionType === deal.customerDeal.commissionType
            );
            return {
                ...site,
                existingLink: existingLink != undefined ? true : false,
                linkStatus: existingLink ? existingLink.status : null,
                isEditing: false,
                isNewSite: false
            };
        });
    return eligibleSites;
}

function expandAllMarkets() {
    const expanded = {};
    transformedMarketDeals.value.forEach(market => {
        expanded[market.marketName] = true;
    });
    expandedRows.value = expanded;
}

function expandAllDeals() {
    transformedMarketDeals.value.forEach(market => {
        const expanded = {};
        market.deals.forEach(deal => {
            expanded[deal.dealId] = true;
        });
        expandedDealsPerMarket.value[market.marketName] = expanded;
    });
}

function expandDeal(deal, marketName) {
    if (!expandedDealsPerMarket.value[marketName]) {
        expandedDealsPerMarket.value[marketName] = {};
    }
    expandedDealsPerMarket.value[marketName][deal.dealId] = true;
}

function getEligibleSitesCount(deal, marketName) {
    const eligibleSites = getEligibleSitesForDeal(deal, marketName);
    const unlinkedSites = eligibleSites.filter(site => !site.existingLink);
    const baseCount = unlinkedSites.length;

    // Add 1 to count for non-SEO users to account for potential new toplist
    return (!store.getters.isSeo)
        ? (baseCount + 1).toString()
        : (baseCount > 0 ? baseCount.toString() : '0');
}

const uniqueMarkets = computed(() => {
    if (!props.advertiser.marketDeals) return [];
    return [...new Set(props.advertiser.marketDeals.map(market => market.marketName))];
});

const availableVerticals = computed(() => {
    if (!props.advertiser.marketDeals) return [];

    const verticals = [
        { name: 'Casino', value: 'casino' },
        { name: 'Sportsbook', value: 'sportsbook' }
    ];

    // Check which verticals have deals
    return verticals.map(vertical => {
        const hasDeals = props.advertiser.marketDeals.some(market =>
            market.deals.some(deal => deal.vertical === vertical.value)
        );
        return {
            ...vertical,
            disabled: !hasDeals
        };
    });
});

const getVerticalTooltip = computed(() => {
    const disabledVerticals = availableVerticals.value
        .filter(v => v.disabled)
        .map(v => v.name)
        .join(', ');

    return disabledVerticals ?
        `No deals available for: ${disabledVerticals}` :
        undefined;
});

// Add this computed property for default vertical
const defaultVertical = computed(() => {
    const hasCasino = props.advertiser.marketDeals?.some(market =>
        market.deals.some(deal => deal.vertical === 'casino')
    );

    const hasSportsbook = props.advertiser.marketDeals?.some(market =>
        market.deals.some(deal => deal.vertical === 'sportsbook')
    );

    return hasCasino ? 'casino' : (hasSportsbook ? 'sportsbook' : null);
});

// Add a watcher for selectedVertical to re-expand rows when it changes
watch(selectedVertical, () => {
    expandAllMarkets();
    // expandAllDeals();
});

const cancelNewSite = (dealId) => {
    newSiteRows.value[dealId] = {
        isNewSite: true,
        isEditing: false,
        name: '',
        existingLink: false
    };
};

// Add this computed or constant for the default row state
const defaultNewSiteRow = {
    isNewSite: true,
    isEditing: false,
    name: '',
    existingLink: false
};



onMounted(async () => {
    await fetchSites();
    selectedVertical.value = defaultVertical.value;
    expandAllMarkets();
    // expandAllDeals();

    console.log('advertiser', props.advertiser);
    // Initialize newSiteRows for each deal
    props.advertiser.marketDeals?.forEach(market => {
        market.deals.forEach(deal => {
            newSiteRows.value[deal.dealId] = { ...defaultNewSiteRow };
        });
    });
});

</script>

<style scoped>
:deep(.p-datatable-tbody > tr.p-row-expanded) {
    background: #f8f9fa;
}

:deep(.p-datatable .p-datatable-tbody > tr.p-row-expanded + tr > td) {
    background: #f8f9fa;
}

:deep(.p-datatable-scrollable) {
    border-radius: 8px;
}

:deep(.p-button.p-button-outlined) {
    background: white;
}

:deep(.p-badge.p-badge-info) {
    background: #3B82F6;
    color: white;
}

:deep(.p-datatable-tbody > tr.p-row-expanded + tr > td > .p-datatable > .p-datatable-wrapper > .p-datatable-thead) {
    display: none;
}

:deep(.deals-datatable .p-datatable-thead) {
    display: none;
}
</style>
